// collectionPointerWriter: the ONE serial queue for the cloud collection
// pointer. Every saved_collections upsert and delete, and the legacy
// user_metadata clear, runs through it, one at a time, in order.
//
// Why a queue: Remove deletes the row and then verifies it is gone. An upsert
// from an import, a salvage Keep or CollectionSync's claim that lands AFTER that
// delete recreates the row, and the "removed" collection comes back on the next
// login. Serializing every pointer write makes "Remove deletes LAST" true by
// construction.
//
// The removal protocol:
//  - Starting a removal is synchronous: it takes the next sequence number and
//    queues behind every write already queued or in flight (those finish
//    first; the delete then removes what they wrote).
//  - Every write carries the sequence number of the operation that produced it
//    (captured when the operation STARTED, see pointerWriteSeq). A write that
//    reaches the front after a SUCCESSFUL removal for its user, from an
//    operation that started before that removal ended, is rejected without a
//    network call (the tombstone). A write from an operation started after the
//    removal is the user creating a new collection: it is allowed, and it
//    clears the tombstone.
//  - A FAILED removal leaves no tombstone, so writes queued behind it simply
//    proceed: a failed Remove discards nobody's work.

let seq = 0
let tail: Promise<unknown> = Promise.resolve()
let removing = false
let tombstone: { userId: string; seq: number } | null = null

/** The sequence number to stamp on an operation at its START. */
export function pointerWriteSeq(): number {
  return seq
}

/** True while a removal is queued or running. */
export function pointerRemovalInProgress(): boolean {
  return removing
}

export type PointerWriteResult<T> = { ok: true; value: T } | { ok: false; rejected: true }

/**
 * Queue one pointer write for `userId`, stamped with the sequence number its
 * operation started at. `fn` performs the network call; it runs only if the
 * write is still allowed when it reaches the front.
 */
export function writePointer<T>(
  userId: string,
  opSeq: number,
  fn: () => Promise<T>,
): Promise<PointerWriteResult<T>> {
  const run = async (): Promise<PointerWriteResult<T>> => {
    if (tombstone && tombstone.userId === userId) {
      if (opSeq < tombstone.seq) return { ok: false, rejected: true }
      // A new collection after the removal: the tombstone has done its job.
      tombstone = null
    }
    return { ok: true, value: await fn() }
  }
  const p = tail.then(run, run)
  tail = p.catch(() => undefined)
  return p
}

/**
 * Run a removal exclusively. Synchronously takes its place in the queue; `fn`
 * runs after every earlier write has finished and before any later one. It
 * reports whether the CLOUD removal happened and for which user (null =
 * nothing in the cloud to remove, e.g. signed out); a success leaves that
 * user's tombstone. Resolves to `fn`'s full result (callers attach their own
 * outcome detail). A throw counts as a failure and is rethrown.
 */
export function removeExclusively<R extends { ok: boolean; userId: string | null }>(
  fn: () => Promise<R>,
): Promise<R> {
  seq += 1
  removing = true
  const run = async (): Promise<R> => {
    let result: R | null = null
    try {
      result = await fn()
      return result
    } finally {
      // Everything that started before (or during) this removal now carries a
      // smaller number than anything that starts after it.
      seq += 1
      removing = false
      if (result?.ok && result.userId) tombstone = { userId: result.userId, seq }
    }
  }
  const p = tail.then(run, run)
  tail = p.catch(() => undefined)
  return p
}

/** Test seam. */
export function __resetCollectionPointerWriter(): void {
  seq = 0
  tail = Promise.resolve()
  removing = false
  tombstone = null
}
